/**
 * Maps a concrete pathname onto the route pattern that produced it.
 *
 * Two of the app's routes carry ids (`/inventory/:asset_class_layer_id/:asset_id/edit`
 * and `/inventory/:asset_class_layer_id/new`), so raw pathnames are effectively
 * unbounded — production shows ~1000 distinct values, dominated by one asset per
 * url. Grouping analytics by the raw path is therefore useless: every asset edit
 * looks like its own page. Masking collapses those to a handful of patterns that
 * funnels and path analysis can actually use.
 *
 * Kept as a pure function (no react-router hooks) so it is directly unit-testable
 * and usable outside a router context. `useMatches()` is not an option here: it is
 * data-router only and throws under the declarative `useRoutes()` setup this app
 * uses (react-router 7).
 *
 * Unmatched paths are returned unchanged rather than bucketed into an "unknown"
 * label, so a route added later is visible in the data instead of silently
 * disappearing.
 */

type RouteMask = {
  readonly pattern: RegExp;
  readonly route: string;
};

const ROUTE_MASKS: readonly RouteMask[] = [
  {
    pattern: /^\/inventory\/[^/]+\/[^/]+\/edit$/,
    route: '/inventory/:asset_class_layer_id/:asset_id/edit',
  },
  {
    pattern: /^\/inventory\/[^/]+\/new$/,
    route: '/inventory/:asset_class_layer_id/new',
  },
];

export function toRoutePattern(pathname: string): string {
  if (!pathname) return pathname;

  // A trailing slash is the same route to the router but a different string to
  // PostHog, which would split one pattern across two rows.
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

  return ROUTE_MASKS.find(({ pattern }) => pattern.test(normalized))?.route ?? normalized;
}
